import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Printer, Download, FileSearch, MessageCircle, Paperclip, Upload, Send, History } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { inr, fmtDate } from "@/lib/format";
import { printBillEnquiry } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUSES = ["All", "Paid", "Partly Paid", "Unpaid"];
const PAPERS = ["All Papers", "Missing POD", "Missing MR Copy", "Missing Bill Copy", "Missing Any", "Complete"];
const billNo = (r) => String(r.invoice_no || "").replace(/^INV[-\s]?/i, "");

export const ageDays = (r) => {
  if (r.status === "Paid" || !r.issue_date) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(r.issue_date).getTime()) / 86400000));
};
const AGE_STYLES = [
  [30, "bg-emerald-100 text-emerald-700", "0–30 days"],
  [60, "bg-amber-100 text-amber-700", "31–60 days"],
  [90, "bg-orange-100 text-orange-700", "61–90 days"],
  [Infinity, "bg-red-100 text-red-700", "90+ days"],
];
const ageStyle = (d) => AGE_STYLES.find(([max]) => d <= max);
const AgeBadge = ({ r }) => {
  const d = ageDays(r);
  if (d == null) return <span className="text-muted-foreground">—</span>;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${ageStyle(d)[1]}`}>{d}d</span>;
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${
      status === "Paid" ? "bg-emerald-100 text-emerald-700" : status === "Partly Paid" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
    }`}
  >
    {status}
  </span>
);

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}

export default function BillEnquiry() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const paperParam = PAPERS.includes(searchParams.get("papers")) ? searchParams.get("papers") : "All Papers";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [branch, setBranch] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detail, setDetail] = useState(null);
  const [hasSearched, setHasSearched] = useState(paperParam !== "All Papers");
  const [customers, setCustomers] = useState([]);
  const [sel, setSel] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [uploading, setUploading] = useState("");
  const [papers, setPapers] = useState(paperParam);
  const [remLogs, setRemLogs] = useState({});
  const [remLogOpen, setRemLogOpen] = useState(false);
  const [respDraft, setRespDraft] = useState({});
  const saveResponse = async (logId, d) => {
    try {
      await api.put(`/reminder-log/${logId}/response`, { response: d.response, response_note: d.note, promised_date: d.promised_date || "" });
      toast.success("Customer reply saved");
      setRespDraft((s) => { const n = { ...s }; delete n[logId]; return n; });
      loadRemLogs();
    } catch (e) {
      toast.error("Could not save reply");
    }
  };
  const [chaseOpen, setChaseOpen] = useState(false);
  const [branchContacts, setBranchContacts] = useState({});
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
  const lastReminder = (name) => remLogs[(name || "").trim().toLowerCase()];
  const loadRemLogs = () =>
    api.get("/reminder-log").then((r) => {
      const m = {};
      (r.data?.rows || []).forEach((lg) => { m[lg.customer_name.trim().toLowerCase()] = lg; });
      setRemLogs(m);
    }).catch(() => {});
  const logReminder = async (customer, bills, total) => {
    try {
      await api.post("/reminder-log", { customer_name: customer, bills, total_balance: total });
      loadRemLogs();
    } catch (e) { /* non-blocking */ }
  };
  const token = localStorage.getItem("ntc_token");
  const fileUrl = (p) => `${API}/files/${p}?auth=${token}`;

  const reload = async (keepDetailId) => {
    const r = await api.get("/bill-enquiry");
    const list = r.data?.rows || [];
    setRows(list);
    if (keepDetailId) setDetail(list.find((x) => x.id === keepDetailId) || null);
  };

  const uploadFile = async (r, kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const urls = {
        bill: `/invoices/${r.id}/bill-copy`,
        pod: `/consignments/${r.shipment_id}/pod`,
        mr: `/invoices/${r.id}/attachment/mr-copy`,
        advice: `/invoices/${r.id}/attachment/payment-advice`,
      };
      await api.post(urls[kind], fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await reload(r.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  useEffect(() => {
    api.get("/bill-enquiry").then((r) => setRows(r.data?.rows || [])).catch(() => setRows([]));
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/branch-contacts").then((r) => setBranchContacts(r.data || {})).catch(() => {});
    loadRemLogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const custPhone = (r) => {
    const c = customers.find((x) => (x.name || "").trim().toLowerCase() === (r.customer_name || "").trim().toLowerCase());
    const p = String(c?.phone || "").replace(/\D/g, "");
    return p.length === 10 ? `91${p}` : p;
  };
  const custCode = (r) =>
    r.customer_code ||
    customers.find((x) => (x.name || "").trim().toLowerCase() === (r.customer_name || "").trim().toLowerCase())?.customer_code ||
    "";

  const sendWhatsApp = (r) => {
    const d = ageDays(r);
    const mrLines = (r.receipts || []).map((m) => `- ${m.mr_no || "MR"} | ${m.date ? fmtDate(m.date) : ""} | Rs ${Number(m.amount).toLocaleString("en-IN")}${m.mode ? ` (${m.mode})` : ""}`).join("\n");
    const msg =
      `NISHA TRANSPORT CORPORATION\n` +
      `Bill Tracking — Bill No. ${billNo(r)}\n\n` +
      `Bill Date: ${fmtDate(r.issue_date)}\n` +
      `Bill Amount: Rs ${Number(r.total).toLocaleString("en-IN")}\n` +
      `Received (incl. TDS/Deduction): Rs ${Number(r.paid).toLocaleString("en-IN")}\n` +
      `Balance Due: Rs ${Number(r.balance).toLocaleString("en-IN")}\n` +
      `Status: ${r.status}${d != null ? ` (${d} days since bill date)` : ""}\n` +
      (mrLines ? `\nPayments received:\n${mrLines}\n` : `\nNo payment received yet.\n`) +
      (r.balance > 0.01 ? `\nKindly arrange the balance payment at the earliest. Thank you.` : `\nThank you for the full payment.`);
    const phone = custPhone(r);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    logReminder(r.customer_name, [billNo(r)], r.balance);
    toast.success(phone ? `Opening WhatsApp for ${r.customer_name}…` : "Opening WhatsApp — pick the customer's contact", {
      description: phone ? "" : "No phone number saved for this customer.",
    });
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "All" && r.status !== status) return false;
      if (branch !== "All" && (r.office || "") !== branch) return false;
      if (papers !== "All Papers") {
        const miss = { pod: !r.pod_path, mr: !r.mr_copy_path, bill: !r.bill_copy_path };
        if (papers === "Missing POD" && !miss.pod) return false;
        if (papers === "Missing MR Copy" && !miss.mr) return false;
        if (papers === "Missing Bill Copy" && !miss.bill) return false;
        if (papers === "Missing Any" && !(miss.pod || miss.mr || miss.bill)) return false;
        if (papers === "Complete" && (miss.pod || miss.mr || miss.bill)) return false;
      }
      const d = (r.issue_date || "").slice(0, 10);
      if (fromDate && (!d || d < fromDate)) return false;
      if (toDate && (!d || d > toDate)) return false;
      if (!term) return true;
      return [r.invoice_no, billNo(r), r.customer_name, custCode(r), r.lr_no, r.from_place, r.destination, r.submission_no]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [rows, q, status, branch, fromDate, toDate, papers, customers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e && e.preventDefault();
    const term = q.trim().toLowerCase();
    if (!term && !fromDate && !toDate) {
      toast.info("Enter a bill number, customer or pick a date range");
      return;
    }
    setHasSearched(true);
    if (fromDate || toDate) {
      if (filtered.length === 0) toast.error("No bills found in this date range");
      else toast.success(`${filtered.length} bill(s) found`);
      return;
    }
    const exact = (rows || []).find((r) => billNo(r).toLowerCase() === term || String(r.invoice_no || "").toLowerCase() === term);
    const target = exact || (filtered.length === 1 ? filtered[0] : null);
    if (target) setDetail(target);
    else if (filtered.length === 0) toast.error("No bill found for this search");
    else toast.success(`${filtered.length} bills matched — showing in list`);
  };

  const mrNos = (r) => (r.receipts || []).map((m) => m.mr_no).filter(Boolean).join(", ");
  const mrDates = (r) => (r.receipts || []).map((m) => (m.date ? fmtDate(m.date) : "")).filter(Boolean).join(", ");
  const mrAmt = (r) => (r.receipts || []).reduce((s, m) => s + (Number(m.amount) || 0), 0);
  const mrTds = (r) => (r.receipts || []).reduce((s, m) => s + (Number(m.tds) || 0), 0);
  const mrDed = (r) => (r.receipts || []).reduce((s, m) => s + (Number(m.deduction) || 0), 0);
  const tdsBand = (r) => [Math.round(r.total * 1) / 100, Math.round(r.total * 2) / 100];
  const tdsMismatch = (r) => {
    const t = mrTds(r);
    if (!(t > 0)) return false;
    const [lo, hi] = tdsBand(r);
    return t < lo - 0.5 || t > hi + 0.5;
  };
  const subDate = (r) => (r.submission_no ? `${r.submission_date ? fmtDate(r.submission_date) : r.submission_no}${r.submission_status ? ` (${r.submission_status})` : ""}` : "—");

  const exportCsv = () => {
    const headers = ["Branch", "Customer", "Code", "LR No.", "LR Date", "Bill No.", "Bill Date", "Bill Amount", "Received Amount", ...(isAdmin ? ["TDS", "Deduction"] : []), "MR No.", "MR Date", "MR Amount", "Balance Amount", "Status", "Bill Submission Date", "Submission Status"];
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      lines.push([r.office, r.customer_name, custCode(r) || "", r.lr_no, r.lr_date, billNo(r), r.issue_date, r.total, mrAmt(r) || "", ...(isAdmin ? [mrTds(r) || "", mrDed(r) || ""] : []), mrNos(r), mrDates(r), mrAmt(r), r.balance, r.status, r.submission_date || r.submission_no || "", r.submission_status]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bill-enquiry_${fromDate || "all"}_${toDate || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkGroups = () => {
    const picked = filtered.filter((r) => sel.has(r.id) && r.balance > 0.01);
    const m = {};
    picked.forEach((r) => (m[r.customer_name] = [...(m[r.customer_name] || []), r]));
    return Object.entries(m).map(([customer, bills]) => ({ customer, bills, total: bills.reduce((s, b) => s + b.balance, 0) }));
  };

  const sendBulkWhatsApp = (g) => {
    const lines = g.bills.map((b) => `- Bill ${billNo(b)} dt ${fmtDate(b.issue_date)} | Rs ${Number(b.total).toLocaleString("en-IN")} | Balance Rs ${Number(b.balance).toLocaleString("en-IN")}${ageDays(b) != null ? ` (${ageDays(b)} days)` : ""}`).join("\n");
    const msg =
      `NISHA TRANSPORT CORPORATION — Payment Reminder\n\nDear ${g.customer},\nThe following bill(s) are pending with us:\n${lines}\n\nTotal Balance Due: Rs ${Number(g.total).toLocaleString("en-IN")}\n\nKindly arrange payment at the earliest. Thank you.`;
    const c = customers.find((x) => (x.name || "").trim().toLowerCase() === g.customer.trim().toLowerCase());
    const p = String(c?.phone || "").replace(/\D/g, "");
    const phone = p.length === 10 ? `91${p}` : p;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    logReminder(g.customer, g.bills.map((b) => billNo(b)), g.total);
  };

  const showList = hasSearched || q || fromDate || toDate || status !== "All";

  const chaseGroups = (() => {
    const m = {};
    (rows || []).forEach((r) => {
      const missing = [!r.pod_path && "POD", !r.mr_copy_path && "MR Copy"].filter(Boolean);
      if (!missing.length) return;
      const br = r.office || "Chennai";
      (m[br] = m[br] || []).push({ bill: billNo(r), customer: r.customer_name, missing });
    });
    return Object.entries(m)
      .map(([branch, bills]) => ({ branch, bills }))
      .sort((a, b) => a.branch.localeCompare(b.branch));
  })();

  const chaseBranchWhatsApp = (g) => {
    const lines = g.bills.map((b, i) => `${i + 1}. Bill ${b.bill} (${b.customer}) — Missing: ${b.missing.join(", ")}`).join("\n");
    const msg =
      `NISHA TRANSPORT CORPORATION — Papers Pending (${g.branch})\n\n` +
      `The following bills have missing documents. Please arrange and upload them in Bill Enquiry:\n\n${lines}\n\n` +
      `Total: ${g.bills.length} bill(s) pending papers. Kindly complete the files at the earliest.`;
    const phone = String(branchContacts[g.branch] || "").replace(/\D/g, "");
    const target = phone ? (phone.length === 10 ? `91${phone}` : phone) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success(target ? `Opening WhatsApp for ${g.branch} branch…` : `Opening WhatsApp — no number saved for ${g.branch}, pick the contact`, {
      description: target ? "" : "Save the branch WhatsApp in NTC Profile → Branch Contacts.",
    });
  };

  return (
    <div>
      <PageHeader title="Bill Enquiry" subtitle="Search and track any bill — payment status, receipts, submission and balance." />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={handleSubmit} className="flex flex-1 min-w-[240px] flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="bill-enquiry-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Enter bill number, customer, LR no…" className="pl-9" />
          </div>
          <Button type="submit" data-testid="bill-enquiry-submit-btn">
            <Search className="mr-2 h-4 w-4" /> Submit
          </Button>
          <div className="flex items-center gap-1.5">
            <Label className="whitespace-nowrap text-xs text-muted-foreground">From</Label>
            <Input type="date" className="w-[150px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="bill-enquiry-from-date" />
            <Label className="whitespace-nowrap text-xs text-muted-foreground">To</Label>
            <Input type="date" className="w-[150px]" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="bill-enquiry-to-date" />
          </div>
          <Select value={branch} onValueChange={(v) => { setBranch(v); setHasSearched(true); }}>
            <SelectTrigger className="w-[130px]" data-testid="bill-enquiry-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Branches</SelectItem>
              {Array.from(new Set((rows || []).map((r) => r.office).filter(Boolean))).sort().map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => { setStatus(v); setHasSearched(true); }}>
            <SelectTrigger className="w-[130px]" data-testid="bill-enquiry-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={papers} onValueChange={(v) => { setPapers(v); setHasSearched(true); }}>
            <SelectTrigger className="w-[160px]" data-testid="bill-enquiry-papers">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{PAPERS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={() => { setQ(""); setStatus("All"); setBranch("All"); setPapers("All Papers"); setFromDate(""); setToDate(""); setHasSearched(false); }} data-testid="bill-enquiry-clear-btn">
            Clear
          </Button>
          <Button type="button" variant="outline" onClick={() => setRemLogOpen(true)} data-testid="bill-enquiry-remlog-btn">
            <History className="mr-2 h-4 w-4" /> Reminder Log
          </Button>
          <Button type="button" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" onClick={() => setChaseOpen(true)} disabled={!chaseGroups.length} data-testid="bill-enquiry-chase-btn">
            <Paperclip className="mr-2 h-4 w-4" /> Papers Chase{chaseGroups.length ? ` (${chaseGroups.reduce((s, g) => s + g.bills.length, 0)})` : ""}
          </Button>
          <Button type="button" variant="outline" onClick={() => printBillEnquiry(filtered, fromDate, toDate, isAdmin)} disabled={!filtered.length} data-testid="bill-enquiry-print-btn">
            <Printer className="mr-2 h-4 w-4" /> Print List
          </Button>
          <Button type="button" variant="outline" onClick={exportCsv} disabled={!filtered.length} data-testid="bill-enquiry-export-btn">
            <Download className="mr-2 h-4 w-4" /> Excel
          </Button>
          {sel.size > 0 && (
            <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setBulkOpen(true)} data-testid="bill-enquiry-bulk-btn">
              <Send className="mr-2 h-4 w-4" /> Reminders ({sel.size})
            </Button>
          )}
        </form>
      </div>

      {!showList ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 py-20 text-center">
          <FileSearch className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-muted-foreground">Enter a bill number, customer name or date range to track a bill.</p>
        </div>
      ) : (
        <TableShell>
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2 text-[11px]" data-testid="bill-enquiry-age-legend">
            <span className="font-bold uppercase tracking-[0.15em] text-slate-500">Overdue Age:</span>
            {AGE_STYLES.map(([, cls, lbl]) => (
              <span key={lbl} className={`rounded-full px-2 py-0.5 font-semibold ${cls}`}>{lbl}</span>
            ))}
            <span className="text-muted-foreground">(days since bill date, unpaid/partly paid bills)</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="w-[36px]">
                  <Checkbox
                    checked={filtered.length > 0 && filtered.every((r) => sel.has(r.id))}
                    onCheckedChange={() => setSel(filtered.every((r) => sel.has(r.id)) ? new Set() : new Set(filtered.map((r) => r.id)))}
                    data-testid="bill-enquiry-select-all"
                  />
                </TableHead>
                {["Branch", "Customer", "Code", "LR No.", "LR Date", "Bill No.", "Bill Date", "Bill Amount", "Received Amount", ...(isAdmin ? ["TDS", "Deduction"] : []), "MR No.", "MR Date", "MR Amount", "Balance Amount", "Status", "Age", "Bill Submission Date", "Files"].map((h) => (
                  <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${["Bill Amount", "TDS", "Deduction", "Received Amount", "MR Amount", "Balance Amount"].includes(h) ? "text-right" : ""}`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 20 : 18} className="py-10 text-center text-muted-foreground">No bills found.</TableCell></TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetail(r)} data-testid="bill-enquiry-row">
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={sel.has(r.id)}
                      onCheckedChange={() => setSel((p) => { const n = new Set(p); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })}
                      data-testid="bill-enquiry-select-row"
                    />
                  </TableCell>
                  <TableCell className="text-sm">{r.office || "—"}</TableCell>
                  <TableCell className="text-sm">{r.customer_name}</TableCell>
                  <TableCell className="text-sm" data-testid="bill-enquiry-code-cell">{custCode(r) || "—"}</TableCell>
                  <TableCell className="text-sm">{r.lr_no || "—"}</TableCell>
                  <TableCell className="text-sm">{r.lr_date ? fmtDate(r.lr_date) : "—"}</TableCell>
                  <TableCell className="text-sm font-semibold text-primary">{billNo(r)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.issue_date)}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{inr(r.total)}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums" data-testid="bill-enquiry-received-cell">{mrAmt(r) ? inr(mrAmt(r)) : "—"}</TableCell>
                  {isAdmin && (
                    <TableCell className={`text-right text-sm tabular-nums ${tdsMismatch(r) ? "font-bold text-red-600" : "text-amber-700"}`} title={tdsMismatch(r) ? `TDS outside expected 1–2% band (${inr(tdsBand(r)[0])}–${inr(tdsBand(r)[1])})` : ""} data-testid="bill-enquiry-tds-cell">
                      {mrTds(r) ? <>{inr(mrTds(r))}{tdsMismatch(r) && <span className="ml-1 rounded bg-red-100 px-1 text-[10px] font-bold text-red-700" data-testid="tds-mismatch-badge">!</span>}</> : "—"}
                    </TableCell>
                  )}
                  {isAdmin && <TableCell className="text-right text-sm tabular-nums text-amber-700" data-testid="bill-enquiry-ded-cell">{mrDed(r) ? inr(mrDed(r)) : "—"}</TableCell>}
                  <TableCell className="text-sm">{mrNos(r) || "—"}</TableCell>
                  <TableCell className="text-sm">{mrDates(r) || "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-emerald-600">{mrAmt(r) ? inr(mrAmt(r)) : "—"}</TableCell>
                  <TableCell className={`text-right text-sm font-semibold tabular-nums ${r.balance > 0.01 ? "text-red-600" : ""}`}>{inr(r.balance)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell><AgeBadge r={r} /></TableCell>
                  <TableCell className="text-sm" data-testid="bill-enquiry-subdate-cell">{subDate(r)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <span className="flex items-center gap-1" data-testid="bill-enquiry-files-cell">
                      {r.bill_copy_path && (
                        <a href={fileUrl(r.bill_copy_path)} target="_blank" rel="noreferrer" className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 hover:bg-blue-200">BILL</a>
                      )}
                      {r.pod_path && (
                        <a href={fileUrl(r.pod_path)} target="_blank" rel="noreferrer" className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-200">POD</a>
                      )}
                      {r.mr_copy_path && (
                        <a href={fileUrl(r.mr_copy_path)} target="_blank" rel="noreferrer" className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700 hover:bg-purple-200">MR</a>
                      )}
                      {r.payment_advice_path && (
                        <a href={fileUrl(r.payment_advice_path)} target="_blank" rel="noreferrer" className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 hover:bg-amber-200">ADV</a>
                      )}
                      {!r.bill_copy_path && !r.pod_path && !r.mr_copy_path && !r.payment_advice_path && <Paperclip className="h-3.5 w-3.5 text-slate-300" />}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      )}

      {detail && (
        <div className="mt-6 rounded-xl border border-border bg-white p-6 shadow-sm" data-testid="bill-enquiry-detail">
          {(() => {
            return (
            <>
              <div className="mb-3 flex items-center justify-between border-b pb-3">
                <h3 className="font-display text-lg font-semibold">Bill No.{billNo(detail)} — Tracking</h3>
                <span className="flex items-center gap-2">
                  <AgeBadge r={detail} />
                  <StatusBadge status={detail.status} />
                  <Button variant="ghost" size="icon" onClick={() => setDetail(null)} data-testid="bill-enquiry-detail-close">✕</Button>
                </span>
              </div>
              <div className="grid gap-x-10 sm:grid-cols-2">
                <div>
                  <Row label="Bill No." value={billNo(detail)} />
                  <Row label="Bill Date" value={fmtDate(detail.issue_date)} />
                  <Row label="Bill Submission Date" value={detail.submission_no ? `${detail.submission_date ? fmtDate(detail.submission_date) : detail.submission_no}${detail.submission_status ? ` — ${detail.submission_status}` : ""}` : "Not submitted yet"} />
                  <Row label="Customer Code" value={detail.customer_code} />
                  <Row label="Customer Name" value={detail.customer_name} />
                  <Row label="Branch" value={detail.office} />
                  <Row label="LR No." value={detail.lr_no} />
                  <Row label="LR Date" value={detail.lr_date ? fmtDate(detail.lr_date) : ""} />
                </div>
                <div>
                  <Row label="From" value={detail.from_place} />
                  <Row label="Destination" value={detail.destination} />
                  <Row label="Bill Amount" value={inr(detail.total)} />
                  <Row label="Received Amount" value={mrAmt(detail) ? inr(mrAmt(detail)) : "—"} />
                  {isAdmin && (
                    <Row
                      label="TDS"
                      value={
                        mrTds(detail)
                          ? tdsMismatch(detail)
                            ? <span className="font-bold text-red-600" data-testid="detail-tds-mismatch">{inr(mrTds(detail))} — outside 1–2% band ({inr(tdsBand(detail)[0])}–{inr(tdsBand(detail)[1])})</span>
                            : inr(mrTds(detail))
                          : "—"
                      }
                    />
                  )}
                  {isAdmin && <Row label="Deduction" value={mrDed(detail) ? inr(mrDed(detail)) : "—"} />}
                  <Row label="Balance Amount" value={<span className={detail.balance > 0.01 ? "font-bold text-red-600" : "font-bold text-emerald-600"}>{inr(detail.balance)}</span>} />
                  <Row label="Last Reminder Sent" value={lastReminder(detail.customer_name) ? fmtWhen(lastReminder(detail.customer_name).sent_at) : "Never"} />
                </div>
              </div>
              <div className="mt-4">
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Money Receipts against this bill</div>
                {(detail.receipts || []).length === 0 ? (
                  <p className="rounded border border-dashed p-3 text-center text-xs text-muted-foreground" data-testid="bill-enquiry-no-mrs">No payment received yet.</p>
                ) : (
                  <div className="rounded border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted">
                        <tr className="text-left [&>th]:p-2"><th>MR No.</th><th>MR Date</th><th className="text-right">Received Amount</th><th className="text-right">TDS</th><th className="text-right">Deduction</th><th>Mode</th></tr>
                      </thead>
                      <tbody>
                        {detail.receipts.map((m, i) => (
                          <tr key={i} className="border-t [&>td]:p-2" data-testid="bill-enquiry-mr-row">
                            <td className="font-semibold">{m.mr_no || "—"}</td>
                            <td>{m.date ? fmtDate(m.date) : "—"}</td>
                            <td className="text-right tabular-nums text-emerald-700">{inr(m.amount)}</td>
                            <td className="text-right tabular-nums">{m.tds ? inr(m.tds) : "—"}</td>
                            <td className="text-right tabular-nums">{m.deduction ? inr(m.deduction) : "—"}</td>
                            <td>{m.mode || "—"}{m.cheque_status === "Bounced" ? <span className="ml-1 font-bold text-red-600">(Bounced)</span> : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="mt-4" data-testid="bill-enquiry-attachments">
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Attachments — Bill, POD, MR Copy &amp; Payment Advice</div>
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                  <div className="rounded border p-2.5">
                    <div className="mb-1.5 text-xs font-semibold">Bill Copy</div>
                    {detail.bill_copy_path ? (
                      <a href={fileUrl(detail.bill_copy_path)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-700 underline" data-testid="bill-copy-view-link">View {detail.bill_copy_filename || "bill copy"}</a>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Not uploaded</p>
                    )}
                    <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline">
                      <Upload className="h-3 w-3" /> {uploading === "bill" ? "Uploading…" : detail.bill_copy_path ? "Replace" : "Upload bill copy"}
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => uploadFile(detail, "bill", e.target.files?.[0])} data-testid="bill-copy-upload-input" />
                    </label>
                  </div>
                  <div className="rounded border p-2.5">
                    <div className="mb-1.5 text-xs font-semibold">POD (Delivery Proof)</div>
                    {detail.pod_path ? (
                      <a href={fileUrl(detail.pod_path)} target="_blank" rel="noreferrer" data-testid="pod-view-link">
                        <img src={fileUrl(detail.pod_path)} alt="POD" className="max-h-24 rounded border object-cover" onError={(e) => { e.target.outerHTML = '<span class="text-xs font-semibold text-emerald-700 underline">View POD file</span>'; }} />
                      </a>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">{detail.shipment_id ? "Not uploaded" : "No linked consignment — upload via POD Upload page after linking the LR"}</p>
                    )}
                    {detail.shipment_id && (
                      <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline">
                        <Upload className="h-3 w-3" /> {uploading === "pod" ? "Uploading…" : detail.pod_path ? "Replace" : "Upload POD"}
                        <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => uploadFile(detail, "pod", e.target.files?.[0])} data-testid="pod-upload-input" />
                      </label>
                    )}
                  </div>
                  <div className="rounded border p-2.5">
                    <div className="mb-1.5 text-xs font-semibold">MR Copy</div>
                    {detail.mr_copy_path ? (
                      <a href={fileUrl(detail.mr_copy_path)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-purple-700 underline" data-testid="mr-copy-view-link">View {detail.mr_copy_filename || "MR copy"}</a>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Not uploaded</p>
                    )}
                    <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline">
                      <Upload className="h-3 w-3" /> {uploading === "mr" ? "Uploading…" : detail.mr_copy_path ? "Replace" : "Upload MR copy"}
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => uploadFile(detail, "mr", e.target.files?.[0])} data-testid="mr-copy-upload-input" />
                    </label>
                  </div>
                  <div className="rounded border p-2.5">
                    <div className="mb-1.5 text-xs font-semibold">Payment Advice</div>
                    {detail.payment_advice_path ? (
                      <a href={fileUrl(detail.payment_advice_path)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-amber-700 underline" data-testid="payment-advice-view-link">View {detail.payment_advice_filename || "payment advice"}</a>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Not uploaded</p>
                    )}
                    <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline">
                      <Upload className="h-3 w-3" /> {uploading === "advice" ? "Uploading…" : detail.payment_advice_path ? "Replace" : "Upload payment advice"}
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => uploadFile(detail, "advice", e.target.files?.[0])} data-testid="payment-advice-upload-input" />
                    </label>
                  </div>
                </div>
              </div>
              <Button className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => sendWhatsApp(detail)} data-testid="bill-enquiry-whatsapp-btn">
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Tracking Summary to {detail.customer_name}
              </Button>
            </>
            );
          })()}
        </div>
      )}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]" data-testid="bill-enquiry-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk Payment Reminders</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground">Reminders grouped by customer (bills with balance only). Tap Send for each customer — WhatsApp opens with all their pending bills in one message.</p>
          <div className="grid gap-2">
            {bulkGroups().map((g) => (
              <div key={g.customer} className="flex items-center justify-between gap-3 rounded border p-2.5" data-testid="bulk-reminder-group">
                <div>
                  <div className="text-sm font-semibold">{g.customer}</div>
                  <div className="text-[11px] text-muted-foreground">{g.bills.length} bill(s) · Balance {inr(g.total)}</div>
                  <div className="text-[11px] text-muted-foreground" data-testid="bulk-last-reminded">Last reminded: {lastReminder(g.customer) ? fmtWhen(lastReminder(g.customer).sent_at) : "Never"}</div>
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => sendBulkWhatsApp(g)} data-testid="bulk-reminder-send-btn">
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Send
                </Button>
              </div>
            ))}
            {bulkGroups().length === 0 && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="bulk-reminder-empty">Selected bills have no pending balance.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={remLogOpen} onOpenChange={setRemLogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[920px]" data-testid="reminder-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Payment Reminder Log</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground">When each customer was last sent a payment reminder — note their reply so follow-ups don't get lost.</p>
          <div className="rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left [&>th]:p-2"><th>Customer</th><th>Last Sent</th><th>Bills</th><th className="text-right">Balance</th><th>By</th><th>Customer Reply</th></tr>
              </thead>
              <tbody>
                {Object.values(remLogs).sort((a, b) => (b.sent_at || "").localeCompare(a.sent_at || "")).map((lg) => {
                  const d = respDraft[lg.id] || { response: lg.response || "", note: lg.response_note || "", promised_date: lg.promised_date || "" };
                  const broken = ["Promised to pay", "Partly paying"].includes(lg.response || "") && lg.promised_date && lg.promised_date < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={lg.id} className="border-t align-top [&>td]:p-2" data-testid="reminder-log-row">
                      <td className="font-semibold">{lg.customer_name}</td>
                      <td className="whitespace-nowrap">{fmtWhen(lg.sent_at)}</td>
                      <td>{(lg.bills || []).join(", ") || "—"}</td>
                      <td className="text-right tabular-nums">{inr(lg.total_balance)}</td>
                      <td>{lg.sent_by || "—"}</td>
                      <td className="min-w-[240px]">
                        <div className="flex items-center gap-1.5">
                          <select
                            className="h-7 rounded border bg-white px-1 text-[11px]"
                            value={d.response}
                            onChange={(e) => setRespDraft((s) => ({ ...s, [lg.id]: { ...d, response: e.target.value } }))}
                            data-testid="reminder-response-select"
                          >
                            <option value="">— reply —</option>
                            {["Promised to pay", "Partly paying", "Disputed", "No reply", "Paid"].map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                          <input
                            type="date"
                            className="h-7 rounded border bg-white px-1 text-[11px]"
                            title="Promised payment date"
                            value={d.promised_date || ""}
                            onChange={(e) => setRespDraft((s) => ({ ...s, [lg.id]: { ...d, promised_date: e.target.value } }))}
                            data-testid="reminder-response-promised-date"
                          />
                          <input
                            className="h-7 w-full rounded border bg-white px-1.5 text-[11px]"
                            placeholder="note / promised date"
                            value={d.note}
                            onChange={(e) => setRespDraft((s) => ({ ...s, [lg.id]: { ...d, note: e.target.value } }))}
                            data-testid="reminder-response-note"
                          />
                          <button
                            type="button"
                            className="rounded bg-slate-800 px-2 py-1 text-[10px] font-bold text-white hover:bg-slate-700 disabled:opacity-40"
                            disabled={!d.response && !d.note}
                            onClick={() => saveResponse(lg.id, d)}
                            data-testid="reminder-response-save"
                          >
                            Save
                          </button>
                        </div>
                        {lg.response && (
                          <div className="mt-1 text-[10px] text-muted-foreground" data-testid="reminder-response-saved">
                            <span className={`mr-1 rounded px-1 py-0.5 font-bold ${lg.response === "Disputed" ? "bg-red-100 text-red-700" : lg.response === "Paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{lg.response}</span>
                            {broken && <span className="mr-1 rounded bg-red-600 px-1 py-0.5 font-bold text-white" data-testid="broken-promise-badge">PROMISE BROKEN</span>}
                            {lg.promised_date ? `promised ${fmtDate(lg.promised_date)} · ` : ""}{lg.response_note || ""} {lg.response_at ? `· ${fmtWhen(lg.response_at)}` : ""}{lg.response_by ? ` · ${lg.response_by}` : ""}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {Object.keys(remLogs).length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-muted-foreground" data-testid="reminder-log-empty">No reminders sent yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={chaseOpen} onOpenChange={setChaseOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[600px]" data-testid="papers-chase-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Papers Chase — Missing POD / MR Copy</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground">WhatsApp each branch its list of bills with missing documents so staff can complete the files.</p>
          <div className="grid gap-2">
            {chaseGroups.map((g) => (
              <div key={g.branch} className="rounded border border-amber-200 bg-amber-50/50 p-3" data-testid="papers-chase-group">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">{g.branch}</span>
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">{g.bills.length} bill{g.bills.length > 1 ? "s" : ""}</span>
                  {!String(branchContacts[g.branch] || "").replace(/\D/g, "") && (
                    <span className="text-[10px] text-muted-foreground">(no branch number saved)</span>
                  )}
                  <Button size="sm" className="ml-auto h-7 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={() => chaseBranchWhatsApp(g)} data-testid="papers-chase-wa-btn">
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp {g.branch}
                  </Button>
                </div>
                <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                  {g.bills.slice(0, 6).map((b) => (
                    <div key={b.bill + b.customer} data-testid="papers-chase-bill">
                      Bill <b>{b.bill}</b> · {b.customer} — <span className="font-semibold text-amber-700">Missing: {b.missing.join(", ")}</span>
                    </div>
                  ))}
                  {g.bills.length > 6 && <div className="text-muted-foreground">+{g.bills.length - 6} more in the WhatsApp message…</div>}
                </div>
              </div>
            ))}
            {chaseGroups.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">All bills have their POD and MR copies. 🎉</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
